import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Check, Minus, Globe, ArrowLeft, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/app/Brand";

// ── FX helpers ────────────────────────────────────────────────────────────────
const LOCAL_FX = {
  GBP: 1.00, USD: 1.27, EUR: 1.17, INR: 106.50, AUD: 1.92, CAD: 1.72,
  SGD: 1.71, JPY: 192.0, AED: 4.66, CHF: 1.12, ZAR: 23.50, BRL: 6.40,
  NZD: 2.07, HKD: 9.90, MXN: 21.70,
};
const SYMBOLS = {
  GBP: "£", USD: "$", EUR: "€", INR: "₹", AUD: "A$", CAD: "C$",
  SGD: "S$", JPY: "¥", AED: "د.إ ", CHF: "CHF ", ZAR: "R", BRL: "R$",
  NZD: "NZ$", HKD: "HK$", MXN: "MX$",
};
const fxConvert = (gbpAmount, currency) => {
  const rate = (LOCAL_FX[currency] || 1) / LOCAL_FX["GBP"];
  if (gbpAmount === 0) return 0;
  return currency === "JPY"
    ? Math.round(gbpAmount * rate)
    : Math.round(gbpAmount * rate * 100) / 100;
};
const fmtPrice = (amount, currency) => {
  const sym = SYMBOLS[currency] || (currency + " ");
  if (currency === "JPY") return `${sym}${Math.round(amount).toLocaleString()}`;
  return `${sym}${amount.toFixed(2)}`;
};

// ── Plan definitions ──────────────────────────────────────────────────────────
// monthlyGBP = per-user monthly price. Annual = monthlyGBP * 10 (2 months free).
const PLANS = [
  {
    key: "free",
    label: "Starter",
    backendTier: "free",
    monthlyGBP: 0,
    description: "For individuals exploring CombinedWork at their own pace.",
    badge: null,
    highlight: false,
    isCustom: false,
    isFree: true,
    cta: "Get started free",
    limits: "1 project · 1 member",
  },
  {
    key: "pro",
    label: "Pro",
    backendTier: "pro",
    monthlyGBP: 9.99,
    description: "The full PM suite for growing teams shipping real projects.",
    badge: null,
    highlight: false,
    isCustom: false,
    isFree: false,
    cta: "Start 7-day free trial",
    limits: "Up to 3 projects · up to 10 members",
  },
  {
    key: "business",
    label: "Business",
    backendTier: "business",
    monthlyGBP: 24.99,
    description: "Advanced finance, compliance, and governance for professional consultancies.",
    badge: "Most Popular",
    highlight: true,
    isCustom: false,
    isFree: false,
    cta: "Start 7-day free trial",
    limits: "Unlimited projects · unlimited members",
  },
  {
    key: "enterprise",
    label: "Enterprise",
    backendTier: "enterprise",
    monthlyGBP: 0,
    description: "Bespoke infrastructure, SSO, and a dedicated success manager.",
    badge: null,
    highlight: false,
    isCustom: true,
    isFree: false,
    cta: "Contact sales",
    limits: "Custom seats, storage & SLA",
  },
];

// ── Feature comparison table ──────────────────────────────────────────────────
// Value: true = checkmark, false = dash, string = label shown in cell
const FEATURES = [
  {
    category: "Core project management",
    rows: [
      { label: "Active projects",     free: "1", pro: "Up to 3", business: "Unlimited", enterprise: "Unlimited" },
      { label: "Team members",        free: "1", pro: "Up to 10", business: "Unlimited", enterprise: "Unlimited" },
      { label: "Tasks & sub-tasks",   free: "50", pro: "Unlimited", business: "Unlimited", enterprise: "Unlimited" },
      { label: "Kanban / Agile board", free: true, pro: true, business: true, enterprise: true },
      { label: "Gantt timeline",      free: false, pro: true, business: true, enterprise: true },
      { label: "Sprint planning",     free: false, pro: true, business: true, enterprise: true },
      { label: "RAID log",            free: false, pro: true, business: true, enterprise: true },
      { label: "Dependency graph",    free: false, pro: true, business: true, enterprise: true },
      { label: "Milestone invoicing", free: false, pro: false, business: true, enterprise: true },
    ],
  },
  {
    category: "Collaboration",
    rows: [
      { label: "Project wiki",         free: true,  pro: true,  business: true,  enterprise: true },
      { label: "Project chat",         free: true,  pro: true,  business: true,  enterprise: true },
      { label: "Meeting notes",        free: false, pro: true,  business: true,  enterprise: true },
      { label: "Daily standup",        free: false, pro: true,  business: true,  enterprise: true },
      { label: "Real-time presence",   free: false, pro: true,  business: true,  enterprise: true },
      { label: "Client sign-off portal",free: false, pro: true, business: true,  enterprise: true },
      { label: "Client portal sharing",free: false, pro: false, business: true,  enterprise: true },
    ],
  },
  {
    category: "AI features",
    rows: [
      { label: "AI project assistant",   free: false, pro: true,          business: true,             enterprise: true },
      { label: "AI steering brief",      free: false, pro: true,          business: true,             enterprise: true },
      { label: "Budget forecast (ML)",   free: false, pro: false,         business: true,             enterprise: true },
      { label: "Margin drift alerts",    free: false, pro: false,         business: true,             enterprise: true },
      { label: "PMO risk alerts",        free: false, pro: true,          business: true,             enterprise: true },
      { label: "AI credits/mo",          free: "50",  pro: "1,000",       business: "Unlimited",      enterprise: "Unlimited" },
    ],
  },
  {
    category: "Finance & billing",
    rows: [
      { label: "Invoice creation",        free: false, pro: "5/mo",   business: "Unlimited", enterprise: "Unlimited" },
      { label: "Recurring invoices",      free: false, pro: false,    business: true,        enterprise: true },
      { label: "Xero integration",        free: false, pro: false,    business: true,        enterprise: true },
      { label: "Budget & burndown",       free: false, pro: false,    business: true,        enterprise: true },
      { label: "Time & expense tracking", free: false, pro: true,     business: true,        enterprise: true },
      { label: "Capacity planning",       free: false, pro: false,    business: true,        enterprise: true },
      { label: "Stripe billing",          free: false, pro: false,    business: true,        enterprise: true },
    ],
  },
  {
    category: "Compliance & governance",
    rows: [
      { label: "Compliance document vault", free: false, pro: false, business: true, enterprise: true },
      { label: "Cryptographic sign-off",    free: false, pro: false, business: true, enterprise: true },
      { label: "Audit trail",               free: false, pro: true,  business: true, enterprise: true },
      { label: "Export to PDF/ZIP",         free: false, pro: true,  business: true, enterprise: true },
      { label: "PMO governance dashboard",  free: false, pro: false, business: true, enterprise: true },
    ],
  },
  {
    category: "CRM & sales",
    rows: [
      { label: "Deals pipeline",          free: false, pro: true,  business: true,  enterprise: true },
      { label: "Contacts",                free: true,  pro: true,  business: true,  enterprise: true },
      { label: "HubSpot integration",     free: false, pro: false, business: true,  enterprise: true },
      { label: "Deal-to-project linking", free: false, pro: true,  business: true,  enterprise: true },
    ],
  },
  {
    category: "Platform & support",
    rows: [
      { label: "API access",                free: false, pro: true,           business: true,           enterprise: true },
      { label: "Custom integrations",       free: false, pro: false,          business: false,          enterprise: true },
      { label: "SSO (SAML / OIDC)",         free: false, pro: false,          business: false,          enterprise: true },
      { label: "Custom data migration",     free: false, pro: false,          business: false,          enterprise: true },
      { label: "Uptime SLA",                free: false, pro: "99.5%",        business: "99.9%",        enterprise: "Custom" },
      { label: "Support",                   free: "Community", pro: "Email",   business: "Priority",     enterprise: "Dedicated CSM" },
      { label: "Onboarding",                free: false, pro: "Self-serve",   business: "Guided setup", enterprise: "White-glove" },
    ],
  },
];

const PLAN_KEYS = ["free", "pro", "business", "enterprise"];

// ── Cell renderer ─────────────────────────────────────────────────────────────
const Cell = ({ value, highlight }) => {
  if (value === true)  return <Check className={`w-4 h-4 mx-auto ${highlight ? "text-violet-600" : "text-violet-400"}`} strokeWidth={2.5} />;
  if (value === false) return <Minus className="w-4 h-4 mx-auto text-gray-300" />;
  return <span className={`text-xs font-mono ${highlight ? "text-violet-600" : "text-gray-500"}`}>{value}</span>;
};

// ── FAQ ───────────────────────────────────────────────────────────────────────
const FAQ = [
  { q: "Can I switch plans at any time?", a: "Yes — upgrades take effect immediately and you're charged pro-rata. Downgrades take effect at the end of your billing period." },
  { q: "What happens to my data if I downgrade to Starter?", a: "Your data is preserved. Projects above the 1-project limit become read-only until you upgrade again or archive the extras." },
  { q: "Is there a free trial on paid plans?", a: "Starter is free forever, no card required. Pro and Business include a 7-day free trial — you'll need to add a card to start it, but you won't be charged until the trial ends. We'll remind you 3 days before your trial ends." },
  { q: "How does per-user billing work?", a: "You're billed for every active team member (invited users who have logged in). Viewers are free and don't count toward your seat total." },
  { q: "Do you offer non-profit or educational discounts?", a: "Yes — contact sales@combinedwork.com with proof of status and we'll apply a 40% discount." },
  { q: "What currencies do you support?", a: "We bill in GBP, USD, EUR, AUD, CAD, SGD, INR, and 7 more currencies. Your local currency is auto-detected at checkout." },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-4 text-left text-sm font-semibold text-gray-900 hover:text-violet-600 transition"
      >
        {q}
        <span className="text-gray-400 ml-4 flex-shrink-0">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="pb-4 text-sm text-gray-500 leading-relaxed">{a}</p>}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [currency, setCurrency] = useState("GBP");
  const [geo, setGeo] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/geo/pricing");
        setGeo(data);
        setCurrency(data.currency || "GBP");
      } catch {
        setGeo({ supported_currencies: ["GBP", "USD", "EUR", "AUD", "CAD", "SGD", "INR", "JPY"] });
      }
    })();
  }, []);

  const displayPrice = (plan) => {
    if (plan.isCustom) return { price: "Custom", sub: "contact us" };
    if (plan.isFree) return { price: "Free", sub: "forever" };
    const monthly = fxConvert(plan.monthlyGBP, currency);
    if (annual) {
      const annualTotal = fxConvert(plan.monthlyGBP * 10, currency);
      const perMonth = currency === "JPY"
        ? Math.round(annualTotal / 12)
        : Math.round((annualTotal / 12) * 100) / 100;
      return {
        price: fmtPrice(perMonth, currency),
        sub: `${fmtPrice(annualTotal, currency)} / year · 2 months free`,
      };
    }
    return { price: fmtPrice(monthly, currency), sub: "per user / month" };
  };

  const userPlan = user?.plan || "free";

  const isCurrentPlan = (plan) => {
    if (plan.isCustom) return user && userPlan === "enterprise";
    return user && userPlan === plan.backendTier;
  };

  const subscribe = async (plan) => {
    if (isCurrentPlan(plan)) return;
    if (plan.isCustom) { window.location.href = "mailto:sales@combinedwork.com?subject=Enterprise%20enquiry"; return; }
    if (!user) { navigate("/auth?mode=signup"); return; }
    if (user.role && user.role !== "owner") {
      toast.error("Only the workspace owner can upgrade the plan.");
      return;
    }
    setBusy(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        tier: plan.backendTier,
        origin_url: window.location.origin,
        currency,
        billing_period: annual ? "annual" : "monthly",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Nav */}
      {!user && (
        <header className="border-b border-gray-100 sticky top-0 z-20 bg-white/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 group">
              <LogoMark size={28} />
              <span className="font-display font-black tracking-tighter text-lg leading-none group-hover:text-violet-600 transition-colors">
                Combined<span className="text-violet-600">Work</span>
              </span>
            </Link>
            <Link to="/auth"><Button variant="ghost" className="rounded-none text-gray-500 hover:text-gray-900" data-testid="pricing-signin">Sign in</Button></Link>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">

        {/* Back link */}
        <button
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate(user ? "/dashboard" : "/")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gray-400 hover:text-gray-900 transition mb-12"
          data-testid="pricing-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-600 text-[10px] font-mono uppercase tracking-[0.3em] px-3 py-1.5 rounded-full mb-5">
            <Zap className="w-3 h-3" /> Simple, transparent pricing
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none text-gray-900">
            One platform.<br />
            <span className="text-violet-600">One bill.</span>
          </h1>
          <p className="text-gray-500 mt-5 text-lg max-w-xl mx-auto">
            Stop paying for Jira, Notion, Slack, and HubSpot separately. CombinedWork replaces them all.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 flex items-center justify-center gap-4" data-testid="billing-toggle">
            <span className={`text-sm ${!annual ? "text-gray-900 font-semibold" : "text-gray-400"}`}>Monthly</span>
            <button
              onClick={() => setAnnual((v) => !v)}
              className={`relative w-12 h-6 rounded-full transition-colors ${annual ? "bg-violet-600" : "bg-gray-200"}`}
              data-testid="annual-toggle"
              aria-label="Toggle annual billing"
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${annual ? "translate-x-7" : "translate-x-1"}`} />
            </button>
            <span className={`text-sm flex items-center gap-2 ${annual ? "text-gray-900 font-semibold" : "text-gray-400"}`}>
              Annual
              <span className="text-[10px] font-mono bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded">
                2 months free
              </span>
            </span>
          </div>

          {/* Currency picker */}
          {geo && (
            <div className="mt-5 inline-flex items-center gap-2 border border-gray-200 bg-gray-50 px-3 py-2 rounded-lg" data-testid="currency-switcher">
              <Globe className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-400">Currency:</span>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="rounded-none h-6 w-20 border-0 font-mono text-xs focus:ring-0 p-0 bg-transparent" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(geo.supported_currencies || ["GBP", "USD", "EUR"]).map((c) => (
                    <SelectItem key={c} value={c} className="font-mono text-xs">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start mb-6">
          {PLANS.map((plan) => {
            const { price, sub } = displayPrice(plan);
            return (
              <div
                key={plan.key}
                className={`relative flex flex-col rounded-xl border transition-all hover:-translate-y-1 ${
                  plan.highlight
                    ? "bg-violet-600 border-violet-600 shadow-xl shadow-violet-200"
                    : "bg-white border-gray-100 hover:shadow-lg hover:shadow-gray-100"
                }`}
                data-testid={`tier-card-${plan.key}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-amber-400 text-amber-900 text-[10px] font-bold uppercase tracking-[0.2em] py-1 px-3 rounded-full whitespace-nowrap" data-testid="most-popular-badge">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="p-6 flex-1 flex flex-col">
                  <div className="mb-5">
                    <h3 className={`text-lg font-bold ${plan.highlight ? "text-white" : "text-gray-900"}`}>{plan.label}</h3>
                    <p className={`text-xs mt-1 leading-relaxed h-10 ${plan.highlight ? "text-violet-100" : "text-gray-400"}`}>{plan.description}</p>
                  </div>

                  <div className="mb-1">
                    <div className="flex items-baseline gap-1">
                      <span className={`font-display font-black text-4xl tracking-tighter ${plan.highlight ? "text-white" : "text-gray-900"}`} data-testid={`tier-price-${plan.key}`}>
                        {price}
                      </span>
                      {!plan.isFree && !plan.isCustom && (
                        <span className={`text-xs ${plan.highlight ? "text-violet-200" : "text-gray-400"}`}>/mo</span>
                      )}
                    </div>
                    <p className={`text-[11px] font-mono mt-0.5 min-h-[16px] ${plan.highlight ? "text-violet-200" : "text-gray-400"}`}>{sub}</p>
                  </div>

                  {plan.monthlyGBP > 0 && !plan.isCustom && currency !== "GBP" && (
                    <p className={`text-[10px] font-mono mb-3 ${plan.highlight ? "text-violet-200" : "text-gray-400"}`} data-testid={`tier-gbp-${plan.key}`}>
                      ≈ £{plan.monthlyGBP}/mo GBP
                    </p>
                  )}

                  <div className={`text-[10px] font-mono mt-1 mb-5 px-2 py-1.5 rounded ${
                    plan.highlight ? "bg-white/10 text-violet-100" : "bg-gray-50 text-gray-500"
                  }`}>
                    {plan.limits}
                  </div>

                  <Button
                    onClick={() => subscribe(plan)}
                    disabled={busy === plan.key || isCurrentPlan(plan)}
                    className={`w-full rounded-lg h-10 text-sm font-semibold transition ${
                      isCurrentPlan(plan)
                        ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-default"
                        : plan.highlight
                        ? "bg-white hover:bg-violet-50 text-violet-600"
                        : "border-2 border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-600 bg-transparent"
                    }`}
                    data-testid={`subscribe-${plan.key}-btn`}
                  >
                    {busy === plan.key ? "Loading…" : isCurrentPlan(plan) ? "Current plan" : plan.cta}
                  </Button>

                  {!plan.isFree && !plan.isCustom && (
                    <p className={`text-[10px] text-center mt-2 ${plan.highlight ? "text-violet-200" : "text-gray-400"}`}>7-day free trial</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature comparison table toggle */}
        <div className="text-center my-12">
          <button
            onClick={() => setShowTable((v) => !v)}
            className="inline-flex items-center gap-2 text-sm text-violet-600 border border-violet-200 hover:border-violet-400 px-5 py-2.5 rounded-lg transition"
            data-testid="toggle-comparison-table"
          >
            <Sparkles className="w-4 h-4" />
            {showTable ? "Hide" : "See full"} feature comparison
          </button>
        </div>

        {/* Feature comparison table */}
        {showTable && (
          <div className="overflow-x-auto mb-16" data-testid="comparison-table">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 pr-6 text-xs uppercase tracking-[0.2em] text-gray-400 font-mono w-56">Feature</th>
                  {PLANS.map((p) => (
                    <th key={p.key} className={`text-center py-3 px-4 text-sm font-bold ${p.highlight ? "text-violet-600" : "text-gray-900"}`}>
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((section) => (
                  <>
                    <tr key={section.category} className="bg-gray-50">
                      <td colSpan={5} className="py-3 px-2 text-[10px] uppercase tracking-[0.25em] text-violet-600 font-mono font-semibold">
                        {section.category}
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr key={row.label} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="py-2.5 pr-6 text-sm text-gray-500">{row.label}</td>
                        {PLAN_KEYS.map((pk) => (
                          <td key={pk} className={`py-2.5 px-4 text-center ${pk === "business" ? "bg-violet-50" : ""}`}>
                            <Cell value={row[pk]} highlight={pk === "business"} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mt-20" data-testid="pricing-faq">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-900">Frequently asked questions</h2>
          {FAQ.map((item) => <FaqItem key={item.q} {...item} />)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center border border-gray-100 bg-gray-50 rounded-2xl p-12">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400 mb-3">Still not sure?</div>
          <h2 className="text-3xl font-bold mb-3 text-gray-900">Talk to a real person.</h2>
          <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
            Our team can help you pick the right plan, migrate your data, and get your team onboarded in a day.
          </p>
          <a
            href="mailto:sales@combinedwork.com"
            className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-violet-700 transition text-sm"
          >
            Book a demo →
          </a>
        </div>

        <div className="text-center mt-8 text-xs text-gray-400">
          Prices shown in {currency} · VAT may apply · All plans include 99.5%+ uptime
        </div>
      </div>
    </div>
  );
};

export default Pricing;
